import { useCallback, useEffect, useRef, useState } from 'react'
import type { FeedMeasurement } from '../../hooks/useFeedMeasurement'
import { SHARED_POST_QUERY_PARAM } from '../../utils/sharedPost'
import type { FeedItem, SelectionSet } from '../../types/feedContract'
import { useOddsStore } from '../../stores/oddsStore'
import {
  trackCopyBetButtonClicked,
  trackShareBet,
  type TrackedCardVariant,
} from '../../services/feedTracking'
import {
  getActionProviderKey,
  getAuthoredActionStakes,
  getPrimaryEntry,
  normalizeCouponType,
} from '../../utils/selectionSet'

const DECIMAL_TRAILING_ZEROES_PATTERN = /\.?0+$/

const KAMBI_SHARE_SOURCE = 'Wager Feed shared post'
const KAMBI_OUTCOME_ID = /^[1-9]\d*(?:_[A-Za-z0-9]+)*$/


function buildKambiCoupon(selectionSet?: SelectionSet | null): string | null {
  if (getActionProviderKey(selectionSet) !== 'kambi') return null

  const entry = getPrimaryEntry(selectionSet)
  const action = entry?.action
  if (!entry || action?.type !== 'betslip' || !Array.isArray(action.outcome_ids)) return null

  const outcomeIDs = action.outcome_ids.map(String).map((id) => id.trim())
  if (outcomeIDs.length === 0 || outcomeIDs.some((id) => !KAMBI_OUTCOME_ID.test(id))) return null

  const couponType = normalizeCouponType(action, outcomeIDs.length)
  if (!couponType) return null

  const parts = [couponType, outcomeIDs.join(',')]
  const stakes = getAuthoredActionStakes(entry, action, couponType, outcomeIDs.length)
  if (stakes?.length) {
    parts.push(stakes.map((stake) => (
      (stake / 1000).toFixed(3).replace(DECIMAL_TRAILING_ZEROES_PATTERN, '')
    )).join(','))
  }
  parts.push('append', encodeURIComponent(KAMBI_SHARE_SOURCE))
  return parts.join('|')
}

function addKambiCouponHash(url: URL, coupon: string): void {
  const currentHash = url.hash.slice(1)
  const queryStart = currentHash.indexOf('?')
  const route = (queryStart >= 0 ? currentHash.slice(0, queryStart) : currentHash) || 'home'
  const currentQuery = queryStart >= 0 ? currentHash.slice(queryStart + 1) : ''
  const params = currentQuery
    .split('&')
    .filter((param) => param && !param.startsWith('coupon='))
  params.push(`coupon=${coupon}`)
  url.hash = `${route}?${params.join('&')}`
}

export function buildSharedPostURL(
  postId: string,
  currentHref?: string,
  selectionSet?: SelectionSet | null,
): string {
  const href = currentHref || (typeof window !== 'undefined' ? window.location.href : '')
  const url = new URL(href, currentHref && typeof window !== 'undefined' ? window.location.href : undefined)
  url.searchParams.set(SHARED_POST_QUERY_PARAM, postId)
  const coupon = buildKambiCoupon(selectionSet)
  if (coupon) addKambiCouponHash(url, coupon)
  return url.toString()
}

/**
 * Copy and share engagement for one card: the operator-facing counters that
 * Wager Studios records, plus the Mixpanel events RSI's own project receives
 * (`services/feedTracking`). Every card type funnels through here, so no card
 * can quietly report to one destination and not the other.
 *
 * The two do not share a trigger. Mixpanel counts the copy button being
 * clicked; the copy counter counts a tail the betting provider took. See
 * `trackCopyClick` below for why they were briefly the same call and what that
 * cost.
 */
export function usePostEngagement(
  item: FeedItem,
  measurement?: FeedMeasurement,
  position = 0,
  variant: TrackedCardVariant = 'full',
) {
  const [copyCount, setCopyCount] = useState(item.copy_count)
  const countRef = useRef(item.copy_count)

  // The tracking payload needs the whole item, but `useSurfaceSetup` rebuilds
  // its item objects on every feed update (including realtime pushes). Reading
  // the item through a ref keeps the callbacks below keyed on the identifiers
  // that actually change their behaviour, instead of re-creating them — and
  // re-rendering every CardActions — on each push.
  const latestItem = useRef(item)
  latestItem.current = item

  useEffect(() => {
    countRef.current = item.copy_count
    setCopyCount(item.copy_count)
  }, [item.copy_count])

  // Mixpanel only, and deliberately separate from `recordCopyIntent` below.
  //
  // The legacy widget tracked the click itself: it sent the event before it
  // touched the betting provider, so a tail the book then refused still
  // counted as a copy. Hanging the event off `recordCopyIntent` instead made
  // it inherit that call site's conditions — the provider accepting the
  // outcomes, and the CTA being a `provider_cart` on the navigation paths.
  // These conditions exclude rejected actions from the click event, so the
  // two now fire independently.
  const trackCopyClick = useCallback(() => {
    const odds = useOddsStore.getState().getOddsForActivity(item.id)
    trackCopyBetButtonClicked(
      latestItem.current,
      position,
      variant,
      Boolean(odds?.suspendedStatus && odds.suspendedStatus !== 'none'),
    )
  }, [item.id, position, variant])

  const recordCopyIntent = useCallback(async () => {
    if (!measurement) return
    const previous = countRef.current
    if (typeof previous === 'number') {
      countRef.current = previous + 1
      setCopyCount(previous + 1)
    }

    const result = await measurement.recordCopyIntent(item.id, item.ranking_version, position)
    if (typeof result?.copy_count === 'number') {
      countRef.current = result.copy_count
      setCopyCount(result.copy_count)
    } else if (typeof previous === 'number') {
      countRef.current = previous
      setCopyCount(previous)
    }
  }, [item.id, item.ranking_version, position, measurement])

  const recordShare = useCallback((shareURL: string) => {
    trackShareBet(latestItem.current, shareURL)
    void measurement?.recordShare(item.id, item.ranking_version, position)
  }, [item.id, item.ranking_version, measurement, position])

  return { copyCount, trackCopyClick, recordCopyIntent, recordShare }
}
